import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Supplier Application Service
export const supplierApplicationService = {
  // Create new supplier application
  async createApplication(applicationData) {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase(applicationData);
      snakeData.user_id = user?.id; // Ensure user_id is set

      const { data, error } = await supabase
        ?.from('supplier_applications')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating supplier application:', error);
      throw error;
    }
  },

  // Get user's applications
  async getMyApplications() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('supplier_applications')?.select(`*,country:countries(*)`)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get single application by ID
  async getApplicationById(applicationId) {
    const { data, error } = await supabase
      ?.from('supplier_applications')?.select(`*,country:countries(*)`)?.eq('id', applicationId)?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Update application status (for transitioning to document upload)
  async updateApplicationStatus(applicationId, status) {
    const { data, error } = await supabase
      ?.from('supplier_applications')?.update({
        application_status: status,
        updated_at: new Date()?.toISOString()
      })?.eq('id', applicationId)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  }
};

// Supplier Document Service
export const supplierDocumentService = {
  // Upload document file to storage
  async uploadDocument(file, userId, applicationId) {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = `${userId}/${applicationId}/${fileName}`;

    const { data, error } = await supabase?.storage
      ?.from('supplier-documents')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;
    return filePath;
  },

  // Create document record in database
  async createDocumentRecord(documentData) {
    const snakeData = toSnakeCase(documentData);

    const { data, error } = await supabase
      ?.from('supplier_documents')?.insert(snakeData)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get documents for an application
  async getDocumentsByApplication(applicationId) {
    const { data, error } = await supabase
      ?.from('supplier_documents')?.select('*')?.eq('application_id', applicationId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  // Get signed URL for private document
  async getDocumentUrl(filePath) {
    const { data, error } = await supabase?.storage
      ?.from('supplier-documents')?.createSignedUrl(filePath, 3600);

    if (error) throw error;
    return data?.signedUrl;
  },

  // Delete document
  async deleteDocument(documentId, filePath) {
    // Delete from storage
    const { error: storageError } = await supabase?.storage
      ?.from('supplier-documents')?.remove([filePath]);

    if (storageError) throw storageError;

    // Delete from database
    const { error: dbError } = await supabase
      ?.from('supplier_documents')?.delete()?.eq('id', documentId);

    if (dbError) throw dbError;
    return true;
  },

  // Get document upload progress summary
  async getDocumentProgress(applicationId) {
    const documents = await this.getDocumentsByApplication(applicationId);

    const requiredTypes = ['business_license', 'export_permit', 'organic_certification'];
    const uploadedTypes = documents?.map(doc => doc?.documentType);

    const progress = {
      total: requiredTypes?.length,
      uploaded: requiredTypes?.filter(type => uploadedTypes?.includes(type))?.length,
      documents: documents
    };

    return progress;
  }
};

// Admin Application Management Service
export const adminApplicationService = {
  // Get all applications (admin only)
  async getAllApplications(filters = {}) {
    try {
      let query = supabase
        ?.from('supplier_applications')
        ?.select(`
          *,
          country:countries(id, name_en, name_zh),
          user:user_profiles!supplier_applications_user_id_fkey(id, email, full_name)
        `);

      // Apply filters
      if (filters?.status) {
        query = query?.eq('application_status', filters?.status);
      }

      const { data, error } = await query?.order('submitted_at', { ascending: false });
      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  },

  // Review application (approve/reject)
  async reviewApplication(applicationId, reviewData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...reviewData,
      reviewedBy: user?.id,
      reviewedAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    });

    const { data, error } = await supabase
      ?.from('supplier_applications')?.update(snakeData)?.eq('id', applicationId)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Approve application and create supplier record
  async approveApplication(applicationId) {
    try {
      // 1. Get application details
      const { data: application, error: fetchError } = await supabase
        ?.from('supplier_applications')
        ?.select('*')
        ?.eq('id', applicationId)
        ?.single();

      if (fetchError || !application) throw new Error('Application not found');

      // 2. Map application data to supplier table
      const supplierData = {
        user_id: application.user_id,
        name_en: application.business_name,
        name_zh: application.business_name,
        country_id: application.country_id,
        contact_email: application.email,
        contact_phone: application.phone,
        is_verified: true,
        description_en: `Registered as ${application.supplier_type}`,
        description_zh: `注册为 ${application.supplier_type}`
      };

      // 3. Create supplier record
      const { data: supplier, error: supplierError } = await supabase
        ?.from('suppliers')
        ?.insert(supplierData)
        ?.select()
        ?.single();

      if (supplierError) throw supplierError;

      // 4. Update user role to 'supplier'
      const { error: roleError } = await supabase
        ?.from('user_profiles')
        ?.update({ role: 'supplier' })
        ?.eq('id', application.user_id);

      if (roleError) {
        console.error('Failed to update user role:', roleError);
      }

      // 5. Update application status
      const { error: applicationError } = await supabase
        ?.from('supplier_applications')
        ?.update({
          application_status: 'approved',
          reviewed_at: new Date()?.toISOString()
        })
        ?.eq('id', applicationId);

      if (applicationError) throw applicationError;

      return toCamelCase(supplier);
    } catch (error) {
      console.error('Error in approveApplication:', error);
      throw error;
    }
  },

  // Verify document (approve/reject)
  async verifyDocument(documentId, verificationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...verificationData,
      verifiedBy: user?.id,
      verifiedAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    });

    const { data, error } = await supabase
      ?.from('supplier_documents')?.update(snakeData)?.eq('id', documentId)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  }
};